import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiUsers } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './Dashboard.module.css';

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: string;
  active: boolean;
  createdAt: string;
  createdBy: string | null;
  creator: {
    name: string;
    email: string;
  } | null;
}

const TeamManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'ADMIN',
  });
  const [createLoading, setCreateLoading] = useState(false);

  const fetchMembers = React.useCallback(async () => {
    try {
      const res = await api.get('/team/list');
      setMembers(res.data.members || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchMembers();
  }, [navigate, fetchMembers]);

  const handleCreateMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      await api.post('/team/create', formData);
      toast.success(t('admin.memberCreated'));
      setFormData({ email: '', password: '', name: '', role: 'ADMIN' });
      setShowCreateModal(false);
      fetchMembers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create member');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await api.put(`/team/${id}/toggle`);
      toast.success(t('admin.memberUpdated'));
      fetchMembers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to toggle member status');
    }
  };

  const handleDelete = async (id: string, email: string) => {
    if (!window.confirm(`${t('admin.confirmDelete')} (${email})`)) return;

    try {
      await api.delete(`/team/${id}`);
      toast.success(t('admin.memberDeleted'));
      fetchMembers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete member');
    }
  };

  const activeCount = members.filter(m => m.active).length;
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN': return { bg: '#9333ea20', color: '#a855f7' };
      case 'ADMIN': return { bg: '#3b82f620', color: '#60a5fa' };
      case 'AFFILIATE_MANAGER': return { bg: '#f59e0b20', color: '#fbbf24' };
      case 'MANAGER': return { bg: '#10b98120', color: '#34d399' };
      default: return { bg: '#71809620', color: '#9ca3af' };
    }
  };

  return (
    <AdminLayout>
      <div className={styles.contentHeader}>
        <div>
          <h1>{t('admin.teamManagement')}</h1>
          <p>Manage team members and their permissions</p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setShowCreateModal(true)}>
          <FiPlus /> {t('admin.createTeamMember')}
        </button>
      </div>

      {/* Stats */}
      <div className={styles.statsGrid} style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#667eea' }}>
            <FiUsers />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{members.length}</div>
            <div className={styles.statLabel}>{t('admin.teamMembers')}</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#10b981' }}>
            <FiUsers />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{activeCount}</div>
            <div className={styles.statLabel}>{t('admin.active')}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('admin.name')}</th>
                  <th>{t('admin.email')}</th>
                  <th>{t('admin.role')}</th>
                  <th>{t('admin.createdBy')}</th>
                  <th>Status</th>
                  <th>{t('admin.date')}</th>
                  <th>{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const roleColors = getRoleBadgeColor(member.role);
                  return (
                    <tr key={member.id}>
                      <td style={{ fontWeight: 600 }}>{member.name}</td>
                      <td>{member.email}</td>
                      <td>
                        <span
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            background: roleColors.bg,
                            color: roleColors.color,
                          }}
                        >
                          {t(`admin.roles.${member.role}`)}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                        {member.creator ? member.creator.name : '-'}
                      </td>
                      <td>
                        <span className={member.active ? styles.statusPaid : styles.statusCancelled}>
                          {member.active ? t('admin.active') : t('admin.inactive')}
                        </span>
                      </td>
                      <td>{new Date(member.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {member.role !== 'SUPER_ADMIN' && (
                            <>
                              <button
                                className={styles.actionBtn}
                                onClick={() => handleToggleActive(member.id, member.active)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '0.75rem',
                                  background: member.active ? '#ef444420' : '#10b98120',
                                  color: member.active ? '#ef4444' : '#10b981',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                }}
                              >
                                {member.active ? t('admin.deactivate') : t('admin.activate')}
                              </button>
                              <button
                                className={styles.actionBtn}
                                onClick={() => handleDelete(member.id, member.email)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '0.75rem',
                                  background: '#ef444420',
                                  color: '#ef4444',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                }}
                              >
                                {t('admin.delete')}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {members.length === 0 && (
              <div className={styles.emptyState}>
                <p>No team members found</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className={styles.modal} onClick={() => setShowCreateModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>{t('admin.createTeamMember')}</h3>
            <form onSubmit={handleCreateMember}>
              <div className={styles.formGroup}>
                <label>{t('admin.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="member@example.com"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('admin.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('admin.password')}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
                <small>Minimum 8 caractères</small>
              </div>
              <div className={styles.formGroup}>
                <label>{t('admin.role')}</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="ADMIN">{t('admin.roles.ADMIN')}</option>
                  <option value="AFFILIATE_MANAGER">{t('admin.roles.AFFILIATE_MANAGER')}</option>
                  <option value="MANAGER">{t('admin.roles.MANAGER')}</option>
                </select>
                <small>
                  {formData.role === 'ADMIN' && 'Accès total à toutes les fonctionnalités'}
                  {formData.role === 'AFFILIATE_MANAGER' && 'Accès uniquement aux affiliations'}
                  {formData.role === 'MANAGER' && 'Accès à tout sauf les comptes Instagram'}
                </small>
              </div>
              <div className={styles.modalActions}>
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  {t('admin.cancel')}
                </button>
                <button type="submit" disabled={createLoading}>
                  {createLoading ? 'Creating...' : t('admin.add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default TeamManagement;
